/**
 * Videos inside closed modals.
 * ---------------------------------------------------------------------------
 *
 * The explainer videos in the plaque / display-cover / magnetic-landmarks
 * modals are `autoplay muted loop`, which is what they need to be once the
 * modal is open. But the modal is closed on load, the <video> measures 0×0, and
 * `autoplay` still makes the browser fetch the whole file — 11 MB across three
 * videos on the CUBE page, roughly ten times everything else on it combined.
 *
 * `preload="none"` does not help: the spec lets autoplay override it, and
 * Chrome does. The only reliable fix is to withhold the URL, so the markup
 * carries `data-src` and this fills it in the first time its modal opens.
 *
 * Falls back to an IntersectionObserver for any deferred video that is not
 * inside a modal, so the attribute means "load when needed" wherever it lands.
 */
(() => {
  const SELECTOR = 'video[data-cf-deferred]';

  /**
   * Swap in a REPLACEMENT element rather than filling the placeholder's src.
   * A `<video autoplay>` decides whether it may autoplay when it first loads a
   * source; attach the source afterwards — via `source.src` + `load()` or via
   * `video.src` — and it sits at currentTime 0 with `play()` resolving anyway.
   * Building the element with its source already in place is the state the
   * browser expects, and it plays.
   */
  function load(placeholder) {
    if (placeholder.dataset.cfDeferred === 'loaded') return placeholder;

    const video = document.createElement('video');

    for (const attribute of placeholder.attributes) {
      if (attribute.name === 'preload' || attribute.name === 'data-cf-deferred') continue;
      video.setAttribute(attribute.name, attribute.value);
    }

    // `muted` is the one that matters: setAttribute writes defaultMuted, which
    // leaves `video.muted` false. An unmuted autoplay video is both blocked by
    // the autoplay policy and, if it slips through, loud.
    video.muted = placeholder.muted || placeholder.hasAttribute('muted');
    video.playsInline = true;

    placeholder.querySelectorAll('source').forEach((original) => {
      const source = document.createElement('source');
      source.src = original.dataset.src || original.src;
      if (original.type) source.type = original.type;
      video.appendChild(source);
    });

    video.dataset.cfDeferred = 'loaded';
    placeholder.replaceWith(video);

    return video;
  }

  function play(video) {
    const started = video.play();
    if (started && started.catch) started.catch(() => {});
  }

  function watch(video) {
    const modal = video.closest('x-modal, dialog, [role="dialog"]');

    if (modal) {
      let current = video;

      // Fetch as early as the modal will admit it...
      modal.addEventListener('dialog:before-show', () => {
        current = load(current);
      });

      // ...but start it only once the modal is actually on screen: Chrome does
      // not autoplay a video that is not being rendered, and before-show fires
      // while the dialog is still hidden. play() here resolves silently but the
      // video never leaves currentTime 0.
      modal.addEventListener('dialog:after-show', () => {
        current = load(current);
        play(current);
      });

      return;
    }

    if (!('IntersectionObserver' in window)) {
      play(load(video));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      play(load(video));
    }, { rootMargin: '200px' });

    observer.observe(video);
  }

  function start() {
    document.querySelectorAll(SELECTOR).forEach(watch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/**
 * Pictures the visitor has not asked for yet, but might at any moment.
 * ---------------------------------------------------------------------------
 *
 * The configurators' option modals (plaque, display cover, "more about the
 * plaque") and their closed info accordions must show their pictures the
 * instant they open, so they used to load eagerly with the page. On a phone
 * that is ~450 KB fighting the first screen's pictures for the same thin pipe.
 *
 * Instead they carry `loading="lazy" data-cf-warm`. Lazy keeps them off the
 * first screen: an image that is not rendered (closed modal, closed
 * <details>) is not fetched. Once the page has loaded — or the visitor first
 * clicks or presses a key, whichever comes first — they are switched to
 * eager, which fetches them into the cache while nobody is waiting. A modal
 * opened before that still works: lazy loading fetches the picture as it
 * appears.
 *
 * Scripts that preload for the same reason (rotation views, cover overlays)
 * queue their work with `window.cfWhenWarm(fn)`, so everything that is "for
 * later" starts together, after the page itself is done.
 */
(() => {
  const queue = [];
  let warmed = false;

  function warm() {
    if (warmed) return;
    warmed = true;

    document.querySelectorAll('img[data-cf-warm]').forEach((img) => {
      img.removeAttribute('data-cf-warm');
      img.loading = 'eager';
    });

    queue.splice(0).forEach((fn) => {
      try { fn(); } catch (error) { console.error(error); }
    });
  }

  window.cfWhenWarm = (fn) => {
    if (warmed) fn();
    else queue.push(fn);
  };

  function warmWhenIdle() {
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 2000 });
    else setTimeout(warm, 200);
  }

  if (document.readyState === 'complete') warmWhenIdle();
  else window.addEventListener('load', warmWhenIdle, { once: true });

  // Not pointerdown: on a phone that is also the start of every scroll, and
  // scrolling is not a sign the visitor is about to open anything.
  ['click', 'keydown'].forEach((type) => {
    window.addEventListener(type, warm, { once: true, capture: true, passive: true });
  });
})();
